// src/pages/Accounting/Reconcile/History/index.tsx

import type React from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { PALETTE } from "../constants";
import { mockAccounts } from "../mockData";
import type { Currency } from "../types";
import { formatAccountLabel } from "../utils";
import { HistoryTable } from "./HistoryTable";
import { RowActionMenu } from "./RowActionMenu";
import { mockHistoryEntries } from "./historyMockData";
import type { HistoryPeriodFilter, ReconciliationHistoryEntry } from "./historyTypes";
import {
  DEFAULT_HISTORY_PERIOD,
  HISTORY_PERIOD_LABELS,
} from "./historyTypes";

const TOP_ACTIONS: { label: string; to: string; primary?: boolean }[] = [
  { label: "Chart of accounts", to: "/accounting/chart-of-accounts" },
  { label: "Register", to: "/accounting/register" },
  { label: "Summary", to: "/accounting/reconcile/summary" },
  { label: "Reconcile", to: "/accounting/reconcile", primary: true },
];

export default function ReconcileHistoryPage() {
  const navigate = useNavigate();

  const defaultAccountId = mockAccounts[0]?.id ?? "";
  const [accountId, setAccountId] = useState<string>(defaultAccountId);
  const [period, setPeriod] = useState<HistoryPeriodFilter>(DEFAULT_HISTORY_PERIOD);
  const [openMenu, setOpenMenu] = useState<{
    entryId: string;
    anchor: HTMLButtonElement;
  } | null>(null);

  const account = mockAccounts.find((a) => a.id === accountId);
  const currency: Currency = account?.currency ?? "CAD";

  const entries = useMemo(
    () => filterByPeriod(
      mockHistoryEntries.filter((e) => e.accountId === accountId),
      period,
    ),
    [accountId, period],
  );

  const handleOpenRowMenu = (entryId: string, anchor: HTMLButtonElement) => {
    setOpenMenu({ entryId, anchor });
  };
  const handleCloseMenu = () => setOpenMenu(null);

  const handleViewStatement = (entry: ReconciliationHistoryEntry) => {
    if (entry.statementUrl) window.open(entry.statementUrl, "_blank");
  };
  const handleViewReport = (entryId: string) => {
    console.log("View report:", entryId);
  };
  const handlePrintReport = (entryId: string) => {
    console.log("Print report:", entryId);
  };
  const handleExportPdf = (entryId: string) => {
    console.log("Export PDF:", entryId);
  };
  const handleUndoReconciliation = (entryId: string) => {
    const ok = window.confirm(
      "Undo this reconciliation? This unlocks the period and clears all cleared marks. Transactions and their edits are kept.",
    );
    if (ok) console.log("Undo reconciliation:", entryId);
  };

  // TODO: wire to real user role when auth is in place
  const canUndo = true;

  return (
    <div style={pageStyle}>
      {/* Breadcrumb + title + top-right actions */}
      <div style={headerStyles.wrap}>
        <div style={headerStyles.crumb}>
          <span style={headerStyles.crumbLink} onClick={() => navigate("/accounting")}>
            Accounting
          </span>
          <span style={headerStyles.crumbSep}>/</span>
          <span style={headerStyles.crumbLink} onClick={() => navigate("/accounting/reconcile")}>
            Reconcile
          </span>
          <span style={headerStyles.crumbSep}>/</span>
          <span style={{ color: PALETTE.ink, fontWeight: 600 }}>History by account</span>
        </div>
        <div style={headerStyles.titleRow}>
          <div style={{ minWidth: 0 }}>
            <h1 style={headerStyles.title}>History by account</h1>
            <div style={headerStyles.sub}>
              Review past reconciliations for this account, download reports, or undo a reconciliation if you need to fix something.
            </div>
          </div>
          <div style={headerStyles.actions}>
            {TOP_ACTIONS.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => navigate(a.to)}
                style={a.primary ? headerStyles.actionBtnPrimary : headerStyles.actionBtn}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filter row */}
      <div style={filterRowStyle}>
        <div>
          <div style={filterLabelStyle}>Account</div>
          <div style={selectWrapStyle}>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              style={selectStyle}
            >
              {mockAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {formatAccountLabel(a)}
                </option>
              ))}
            </select>
            <ChevronDown size={14} color={PALETTE.ink4} style={selectIconStyle} />
          </div>
        </div>
        <div>
          <div style={filterLabelStyle}>Report period</div>
          <div style={selectWrapStyle}>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as HistoryPeriodFilter)}
              style={selectStyle}
            >
              {(Object.keys(HISTORY_PERIOD_LABELS) as HistoryPeriodFilter[]).map((p) => (
                <option key={p} value={p}>
                  {HISTORY_PERIOD_LABELS[p]}
                </option>
              ))}
            </select>
            <ChevronDown size={14} color={PALETTE.ink4} style={selectIconStyle} />
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: "0 32px 60px" }}>
        <HistoryTable
          entries={entries}
          currency={currency}
          onViewStatement={handleViewStatement}
          onOpenRowMenu={handleOpenRowMenu}
        />
      </div>

      {/* Row action menu */}
      {openMenu && (
        <RowActionMenu
          entryId={openMenu.entryId}
          anchor={openMenu.anchor}
          canUndo={canUndo}
          onViewReport={handleViewReport}
          onPrintReport={handlePrintReport}
          onExportPdf={handleExportPdf}
          onUndoReconciliation={handleUndoReconciliation}
          onClose={handleCloseMenu}
        />
      )}
    </div>
  );
}

function filterByPeriod(
  entries: ReconciliationHistoryEntry[],
  period: HistoryPeriodFilter,
): ReconciliationHistoryEntry[] {
  if (period === "all-time") return entries;
  const now = new Date();
  let cutoff: Date;
  if (period === "year-to-date") {
    cutoff = new Date(now.getFullYear(), 0, 1);
  } else {
    const days = period === "last-30-days" ? 30 : period === "last-90-days" ? 90 : 365;
    cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - days);
  }
  const cutoffIso = cutoff.toISOString().slice(0, 10);
  return entries.filter((e) => e.statementEndingDate >= cutoffIso);
}

const pageStyle: React.CSSProperties = {
  width: "100%",
  margin: 0,
  padding: "28px 0 90px",
  background: PALETTE.bg,
  minHeight: "100vh",
};

const headerStyles: Record<string, React.CSSProperties> = {
  wrap: { padding: "0 32px 14px" },
  crumb: {
    fontSize: 13,
    color: PALETTE.ink3,
    marginBottom: 4,
    fontWeight: 500,
  },
  crumbLink: { color: PALETTE.ink3, cursor: "pointer" },
  crumbSep: { color: PALETTE.ink3, margin: "0 6px" },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: 800,
    margin: 0,
    letterSpacing: "-0.02em",
    color: PALETTE.ink,
  },
  sub: {
    fontSize: 13.5,
    color: PALETTE.ink3,
    marginTop: 6,
    fontWeight: 500,
    maxWidth: 720,
  },
  actions: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexShrink: 0,
  },
  actionBtn: {
    background: "#fff",
    border: `1px solid ${PALETTE.line}`,
    color: PALETTE.ink,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 14px",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  actionBtnPrimary: {
    background: "#0E1A1A",
    color: "#fff",
    border: 0,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 16px",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
  },
};

const filterRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 20,
  padding: "24px 32px",
  alignItems: "flex-end",
};
const filterLabelStyle: React.CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink2,
  fontWeight: 600,
  marginBottom: 7,
};
const selectWrapStyle: React.CSSProperties = {
  position: "relative",
  display: "inline-block",
};
const selectStyle: React.CSSProperties = {
  padding: "10px 36px 10px 14px",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 8,
  background: "#fff",
  fontSize: 14,
  color: PALETTE.ink,
  fontWeight: 500,
  fontFamily: "inherit",
  cursor: "pointer",
  minWidth: 260,
  appearance: "none",
  WebkitAppearance: "none",
  outline: "none",
};
const selectIconStyle: React.CSSProperties = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
};